<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

// Real avg FIRST-response-time for the conversation-list footer: the current agent's own value
// (hero) + the account/team benchmark (muted). Data from the Smarticks overlay endpoint
// GET /api/v1/accounts/:id/smarticks/response_time (membership-only; mine forced to the caller).
// Replaces the old hard-coded "6 min" mock. Never renders a fake number: distinct loading /
// no-data / error states, and "—" (not 0) when a scope has no data.

const accountId = useMapGetter('getCurrentAccountId');

const STATUS = { LOADING: 'loading', OK: 'ok', EMPTY: 'empty', ERROR: 'error' };
const status = ref(STATUS.LOADING);
const mine = ref(null); // seconds | null
const overall = ref(null); // seconds | null
const agentCount = ref(0);
const series = ref([]); // mine daily values (seconds)

// seconds -> single Hebrew unit. QA-4: sub-minute must NOT show "0 דק׳".
const fmt = seconds => {
  if (seconds === null || seconds === undefined) return null;
  const s = Number(seconds);
  if (Number.isNaN(s)) return null;
  if (s < 60) return { value: String(Math.max(1, Math.round(s))), unit: 'שנ׳' };
  if (s < 3600) return { value: String(Math.round(s / 60)), unit: 'דק׳' };
  return { value: (s / 3600).toFixed(1), unit: 'שע׳' };
};

// solo (1-agent account) OR both scopes round to the same display -> one number, no labels (UX-2/QA-5)
const isSolo = computed(() => {
  if (agentCount.value <= 1) return true;
  if (mine.value === null || overall.value === null) return false;
  const a = fmt(mine.value);
  const b = fmt(overall.value);
  return a && b && a.value === b.value && a.unit === b.unit;
});

const mineDisplay = computed(() => fmt(mine.value));
const overallDisplay = computed(() => fmt(overall.value));

// sparkline only if there are >=2 non-flat data points (UI-4)
const sparkPoints = computed(() => {
  const vals = (series.value || []).filter(v => Number.isFinite(v));
  if (vals.length < 2 || Math.max(...vals) === Math.min(...vals)) return '';
  const W = 80;
  const H = 20;
  const max = Math.max(...vals);
  const min = Math.min(...vals);
  const range = max - min || 1;
  return vals
    .map((v, i) => {
      const x = (i / (vals.length - 1)) * W;
      const y = H - ((v - min) / range) * H;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
});

const reportPath = computed(
  () => `/app/accounts/${accountId.value}/reports/overview`
);

const load = async () => {
  const id = accountId.value;
  if (!id) return;
  status.value = STATUS.LOADING;
  try {
    // ~5-min freshness is fine for a slow-moving aggregate; one call on mount / account switch.
    const tz = -new Date().getTimezoneOffset() / 60;
    // window.axios carries the auth headers; a bare `import axios` does not — this widget
    // had been 401ing on every page load and silently rendering its error state.
    const { data } = await window.axios.get(
      `/api/v1/accounts/${id}/smarticks/response_time`,
      { params: { timezone_offset: tz } }
    );
    mine.value = data.mine;
    overall.value = data.overall;
    agentCount.value = data.agent_count || 0;
    series.value = data.series || [];
    // account has no first-response data at all -> the "will fill in" empty state
    status.value = overall.value === null ? STATUS.EMPTY : STATUS.OK;
  } catch (e) {
    // QA-3: never surface to ChatList; dim "—", silent.
    status.value = STATUS.ERROR;
  }
};

onMounted(load);
watch(accountId, load); // account switch only — NOT coupled to the conversation list / scroll
</script>

<template>
  <router-link
    :to="reportPath"
    dir="auto"
    class="block px-3 py-3 border-t border-n-weak hover:bg-n-alpha-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-n-brand"
  >
    <!-- LOADING: skeleton, reduced-motion guarded -->
    <div v-if="status === 'loading'" class="space-y-1.5">
      <div class="w-24 h-2 rounded bg-n-slate-3 motion-safe:animate-pulse" />
      <div class="w-16 h-6 rounded bg-n-slate-3 motion-safe:animate-pulse" />
    </div>

    <!-- EMPTY: no data yet — never a fake number -->
    <div v-else-if="status === 'empty'">
      <p class="text-xxs text-n-slate-11">
        {{ $t('SMARTICKS.RESPONSE_TIME.TITLE') }}
      </p>
      <p class="mt-0.5 text-2xl font-semibold leading-none text-n-slate-10">
        —
      </p>
      <p class="mt-1 text-xxs text-n-slate-10">
        {{ $t('SMARTICKS.RESPONSE_TIME.EMPTY') }}
      </p>
    </div>

    <!-- OK / ERROR (error renders a dim "—" hero, silent) -->
    <div v-else>
      <div class="flex items-end justify-between">
        <div>
          <p class="text-xxs text-n-slate-11">
            {{ $t('SMARTICKS.RESPONSE_TIME.TITLE')
            }}<template v-if="!isSolo">
              · {{ $t('SMARTICKS.RESPONSE_TIME.MINE') }}
            </template>
          </p>
          <p class="mt-0.5 leading-none">
            <template v-if="mineDisplay">
              <span class="text-2xl font-semibold text-n-slate-12">{{
                mineDisplay.value
              }}</span>
              <span class="text-xs ms-0.5 text-n-slate-11">{{
                mineDisplay.unit
              }}</span>
            </template>
            <span v-else class="text-2xl font-semibold text-n-slate-10">—</span>
          </p>
        </div>
        <svg
          v-if="sparkPoints"
          viewBox="0 0 80 20"
          width="80"
          height="20"
          class="overflow-visible text-n-brand"
          aria-hidden="true"
        >
          <polyline
            :points="sparkPoints"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
      <!-- team benchmark (hidden on solo accounts) + labeled window -->
      <p class="mt-1 text-xxs text-n-slate-10">
        <template v-if="!isSolo && overallDisplay">
          {{ $t('SMARTICKS.RESPONSE_TIME.OVERALL') }}
          {{ overallDisplay.value }} {{ overallDisplay.unit }} ·
        </template>
        {{ $t('SMARTICKS.RESPONSE_TIME.WINDOW') }}
      </p>
    </div>
  </router-link>
</template>
